import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { useParams } from 'react-router-dom';
import { Loading } from '../../../../../components/Loading';
import { Gr8piFormattedDate } from '../../../../../components/Gr8piFormattedDateTime';
import { activeFileImport } from '../../../../../services/apps/RespondentServices';
import { growlActionSuccess, growlErrors } from '../../../../../utils/NotificationUtil';
import { getFileStatus } from './ImportFileResComponent';
import { NotificationContext } from '../../../../../components/GrowlNotifications';
import ValidImportDefinitionContainer from './valid-import/ValidImportDefinitionContainer';
import ActivateConfirmModal from './ActivateConfirmModal';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { AccountContext } from '../../../../../components/AccountContext';
import useSubscriptionExpiredRestrictions from '../../../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  successNoti: { id: 'noti.success', defaultMessage: 'Success!' },
  activate: { id: 'actions.activate', defaultMessage: 'Activate' },
});

const GET_DETAIL_FILE_IMPORT = gql`
  query importRes($accountId: ID!, $respondentImportId: ID!) {
    account(id: $accountId) {
      id
      respondentImport(respondentImportId: $respondentImportId) {
        id
        fileName
        status
        messages
        createdOn
        updatedOn
      }
    }
  }
`;

const DEFAULT_ACCOUNT_STATE = {
  id: null,
  respondentImport: {
    id: null,
    fileName: null,
    status: null,
    messages: [],
    createdOn: null,
    updatedOn: null,
  },
};

const ImportFileResDetail = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();
  const { formatMessage } = useIntl();
  const { importFileId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isActiveModal, setIsActiveModal] = useState(false);
  const [account, setAccount] = useState(DEFAULT_ACCOUNT_STATE);

  const variables = { accountId: currentAccount, respondentImportId: importFileId };

  const { loading, data: queryData } = useQuery(GET_DETAIL_FILE_IMPORT, {
    variables,
  });

  useEffect(() => {
    if (queryData && queryData.account) {

      setAccount(queryData.account);
    }
  }, [queryData]);

  const acceptActive = async (formValues) => {
    const { overwrite } = formValues;
    const input = {
      importId: importFileId,
      accountId: currentAccount,
      overwrite,
    };

    setIsActiveModal(false);
    setIsLoading(true);
    try {
      await activeFileImport(input);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (graphqlResponse) {
      growlErrors(growl, graphqlResponse);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) return <Loading />;

  const { respondentImport } = account;

  const { fileName, status, messages: importMsg, createdOn, updatedOn } = respondentImport;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid wrapper">
            <div className="p-col-12">
              <Panel header={fileName}>
                {status === 'VALID_IMPORT' && (
                  <Button
                    label={formatMessage(messages.activate)}
                    type="button"
                    disabled={isLoading || subscriptionIsExpired}
                    onClick={() => setIsActiveModal(true)}
                  />
                )}
                <div className="details">
                  <span className="details-title">
                    <FormattedMessage id="attrs.status" defaultMessage="Status" />
                  </span>
                  :<span>{getFileStatus(status)}</span>
                </div>
                <div className="details">
                  <span className="details-title">
                    <FormattedMessage id="attrs.created.verbose" defaultMessage="Created on" />
                  </span>
                  :
                  <span>
                    <Gr8piFormattedDate dateValue={createdOn} />
                  </span>
                </div>
                <div className="details">
                  <span className="details-title">
                    <FormattedMessage id="attrs.updated.verbose" defaultMessage="Last update on" />
                  </span>
                  :
                  <span>
                    <Gr8piFormattedDate dateValue={updatedOn} />
                  </span>
                </div>
                {status === 'FAILED_IMPORT' && (
                  <div className="panel panel-default panel-padded mt25">
                    <div className="panel-heading">
                      <FormattedMessage id="file_import.actions.showLog" defaultMessage="Show Log" />
                    </div>
                    <div className="panel-body">
                      {importMsg.map((message, index) => {
                        const elementKey = index;
                        return (
                          <div key={elementKey}>
                            <span>{`${index + 1}. ${message}`}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {status === 'VALID_IMPORT' && (
                  <div>
                    <hr />
                    <h2 className="section-title">
                      <FormattedMessage id="file_import.definition" defaultMessage="Definitions" />
                    </h2>
                    <ValidImportDefinitionContainer />
                  </div>
                )}
              </Panel>
            </div>
          </div>
          <ActivateConfirmModal
            modalOpen={isActiveModal}
            reject={() => setIsActiveModal(false)}
            confirm={acceptActive}
          />
        </div>
      </div>
    </div>
  );
};

export default ImportFileResDetail;
